import React from 'react';
import { cn } from '../../lib/utils';
import { CheckCircle, XCircle } from 'lucide-react';

interface AdvantagesTableProps {
  content: string;
  className?: string;
}

export const AdvantagesTable: React.FC<AdvantagesTableProps> = ({ content, className }) => {
  // Parse the advantages/disadvantages table
  const lines = content.trim().split('\n').filter(line => line.trim());
  if (lines.length < 3) return null; // Must have header, separator, and at least one data row

  // Extract data rows (skip header and separator)
  const dataRows = lines.slice(2).map(line => {
    const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
    return {
      advantage: cells[0] || '',
      disadvantage: cells[1] || ''
    };
  }).filter(row => row.advantage || row.disadvantage);

  if (dataRows.length === 0) return null;

  return (
    <div className={cn("w-full my-6", className)}>
      <div className="grid md:grid-cols-2 gap-6">
        {/* Advantages Column */}
        <div className="bg-green-50 rounded-lg border border-green-200 overflow-hidden">
          <div className="bg-green-100 px-6 py-4 border-b border-green-200">
            <h3 className="text-lg font-semibold text-green-800 flex items-center gap-2">
              <CheckCircle className="text-green-600" size={20} />
              Advantages
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {dataRows.map((row, index) => (
              row.advantage && (
                <div key={`adv-${index}`} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-700 leading-relaxed text-left">{row.advantage}</p>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Disadvantages Column */}
        <div className="bg-red-50 rounded-lg border border-red-200 overflow-hidden">
          <div className="bg-red-100 px-6 py-4 border-b border-red-200">
            <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
              <XCircle className="text-red-600" size={20} />
              Disadvantages
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {dataRows.map((row, index) => (
              row.disadvantage && (
                <div key={`dis-${index}`} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-700 leading-relaxed text-left">{row.disadvantage}</p>
                </div>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};